/**
 * Preflight step — first thing the wizard does.
 *
 * Catches the two preconditions in Google Ads that otherwise cause confusing
 * late errors (Customer Data Terms unaccepted; ECL toggle off). Both are
 * fixable only in the Google Ads UI; we deep-link the right account and
 * offer a "Re-check" button so the user doesn't have to restart.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { offlineConversionsApi } from '@/services/api/offlineConversions';
import type { ECLPreflightResult } from '@/services/api/offlineConversions';

interface Props {
  googleAdsAccountId: string;
  onPassed: (result: ECLPreflightResult) => void;
}

export const ECLPrerequisitesStep: React.FC<Props> = ({
  googleAdsAccountId,
  onPassed,
}) => {
  const query = useQuery({
    queryKey: ['offline-conversions-preflight', googleAdsAccountId],
    queryFn: () => offlineConversionsApi.preflight(googleAdsAccountId),
    refetchOnWindowFocus: false,
  });

  const result = query.data ?? null;
  const passed = Boolean(
    result?.customer_data_terms_accepted &&
      result?.enhanced_conversions_for_leads_enabled
  );

  // Auto-advance when the preflight passes. Hooks must be called
  // unconditionally — gate inside the effect, not on it.
  React.useEffect(() => {
    if (result && passed) onPassed(result);
    // onPassed is intentionally not a dep: parent owns identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passed, result?.customer_data_terms_accepted, result?.enhanced_conversions_for_leads_enabled]);

  if (query.isLoading) {
    return <p className="text-sm text-gray-600">Checking your Google Ads account…</p>;
  }

  if (query.isError || !result) {
    return (
      <Alert variant="error">
        Failed to check account prerequisites.{' '}
        <Button variant="ghost" onClick={() => query.refetch()}>
          Retry
        </Button>
      </Alert>
    );
  }

  const issues: string[] = [];
  if (!result.customer_data_terms_accepted) {
    issues.push('Customer Data Terms not accepted in Google Ads.');
  }
  if (!result.enhanced_conversions_for_leads_enabled) {
    issues.push('Enhanced conversions for leads is turned off.');
  }

  const targetCustomer = result.conversion_tracking_customer_id ?? googleAdsAccountId;
  const settingsHref = `https://ads.google.com/aw/conversions/customersettings?ocid=${targetCustomer}`;

  if (issues.length > 0) {
    return (
      <div className="space-y-3">
        <Alert variant="warning">
          Your Google Ads account needs a one-time setup before Flouri can
          upload offline conversions for it.
        </Alert>
        <ul className="ml-5 list-disc text-sm text-gray-700">
          {issues.map((i) => (
            <li key={i}>{i}</li>
          ))}
        </ul>
        {result.conversion_tracking_customer_id &&
          result.conversion_tracking_customer_id !== googleAdsAccountId && (
            <p className="text-sm text-gray-600">
              Your conversions are tracked at the manager account{' '}
              <code>{result.conversion_tracking_customer_id}</code>. Accept the
              Customer Data Terms on the manager account, not on this client.
            </p>
          )}
        <div className="flex gap-2">
          <a href={settingsHref} target="_blank" rel="noopener noreferrer">
            <Button variant="primary">Open Google Ads settings</Button>
          </a>
          <Button variant="secondary" onClick={() => query.refetch()}>
            Re-check
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Alert variant="success">
      Account is ready. Customer Data Terms accepted and Enhanced Conversions
      for Leads is enabled.
    </Alert>
  );
};
